import * as React from 'react';
import {
  makeStyles,
  createStyles,
  Theme,
} from '@material-ui/core/styles';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import IconButton from '@material-ui/core/IconButton';
import Tooltip from '@material-ui/core/Tooltip';
import Chip from '@material-ui/core/Chip';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import { SkeletonLoader } from '../../components/loading/SkeletonLoader';

// A second page-shell standard alongside PageContentLayout.tsx (same root/backButton naming
// conventions), for pages that drill down into a single record rather than list/filter many —
// explicit request to keep the large banner-style header (big title, coloured banner, a more
// detailed subtitle line) that MatterDetails.tsx already had, rather than PageContentLayout's
// own compact 14px header, while still standardising the back button/skeleton/shell pattern so
// other drill-down pages can reuse it instead of hand-rolling their own banner.
const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      backgroundColor: theme.palette.grey[50],
    },

    banner: {
      backgroundColor: '#eaf6ff',
      // Same drop shadow PageContentLayout's own header/toolbar uses (controlPanel).
      boxShadow: '0 2px 4px rgba(0,0,0,0.07)',
      padding: theme.spacing(3, 4),
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(2),
      flexShrink: 0,
      // Without this, the flush-docked tab rail below (a normal-flow, non-positioned sibling)
      // paints over the banner's own shadow at the seam where they touch — a positioned
      // element always paints after static siblings regardless of DOM order, which puts the
      // shadow back on top where it belongs.
      position: 'relative',
      zIndex: 1,
      [theme.breakpoints.down('xs')]: {
        padding: theme.spacing(2, 2.5),
      },
    },
    // Same back-button treatment as FilteredVerticalTabPage.tsx (ApplicationSettingsPage's own
    // /applicationSettings/LabelSettings shell) — a plain icon button, no circular background/
    // border, immediately followed by a vertical divider line before the title.
    backButton: {
      color: theme.palette.text.secondary,
      padding: 6,
      flexShrink: 0,
      // Same small nudge-left on hover as the Enquiry form's header back link (headerBackLink in enquiryFormDrawerStyles.ts).
      '& .MuiSvgIcon-root': {
        transition: 'transform 0.15s ease',
      },
      '&:hover': {
        backgroundColor: theme.palette.grey[100],
        color: theme.palette.text.primary,
        '& .MuiSvgIcon-root': {
          transform: 'translateX(-2px)',
        },
      },
      '@media (prefers-reduced-motion: reduce)': {
        '&:hover .MuiSvgIcon-root': {
          transform: 'none',
        },
      },
    },
    // alignSelf:'stretch' overrides the banner's own centering just for this element, so it
    // spans the full height of the row (the margin above/below is the "some space" gap rather
    // than a fixed height that stops matching once the banner grows taller/shorter).
    backDivider: {
      width: 1,
      alignSelf: 'stretch',
      margin: theme.spacing(1, 0),
      backgroundColor: theme.palette.divider,
      flexShrink: 0,
    },
    headerMain: {
      flex: 1,
      minWidth: 0,
    },
    // Explicit request: the actions button sits right next to the title (matching the pre-
    // RecordDetailLayout HeaderTitleWrapper/ActionContainer layout MatterDetails.tsx used to
    // have) rather than at the far right of the whole banner — far enough from the title that
    // people were missing it.
    titleRow: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
    },
    title: {
      fontSize: 26,
      fontWeight: 700,
      color: theme.palette.text.primary,
      lineHeight: 1.25,
      wordBreak: 'break-word',
      [theme.breakpoints.down('xs')]: {
        fontSize: 20,
      },
    },
    metaRow: {
      display: 'flex',
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: theme.spacing(1),
      marginTop: theme.spacing(1.25),
    },
    metaChip: {
      height: 26,
      borderRadius: 14,
      backgroundColor: theme.palette.background.paper,
      border: `1px solid ${theme.palette.divider}`,
      '& .MuiChip-label': {
        padding: '0 10px',
        fontSize: 12.5,
        display: 'flex',
        alignItems: 'center',
        gap: 4,
      },
    },
    metaChipLabel: {
      fontWeight: 500,
      color: theme.palette.text.secondary,
    },
    metaChipValue: {
      fontWeight: 700,
      color: theme.palette.text.primary,
    },
    actions: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
      flexShrink: 0,
    },

    // display:flex + flexDirection:column so the single child (a tab list, or any other
    // content) can itself claim `flex:1, minHeight:0` and get a real bounded height to fill —
    // without this, a child that wants to stretch full-height (e.g. an ag-grid) collapses to
    // its own min-content size instead, since a plain block child ignores a scrollable
    // ancestor's available space and just sizes to its own content.
    // 20px matches PageContentLayout.tsx's own content Box (padding="20px 20px") — the shared
    // body-padding convention across page shells, see CLAUDE.md's "Body Section Padding" rule.
    body: {
      flex: 1,
      minHeight: 0,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'auto',
      padding: 20,
      [theme.breakpoints.down('xs')]: {
        padding: theme.spacing(2),
      },
    },
    // Opt-in: a docked side panel (e.g. a tab rail) should touch the body's own left/top
    // edges instead of floating inside padding — the panel then owns its own internal spacing.
    bodyFlush: {
      padding: 0,
      [theme.breakpoints.down('xs')]: {
        padding: 0,
      },
    },
  })
);

export interface RecordDetailMetaItem {
  label: string;
  value: React.ReactNode;
  /** @default theme.palette.text.primary */
  valueColor?: string;
}

export interface RecordDetailLayoutProps {
  /** Main record title (e.g. matter title) — omit to hide the header entirely */
  title: string;
  /** Short, detailed meta line rendered under the title (e.g. file number • status • last updated) */
  meta?: RecordDetailMetaItem[];
  /** Renders a back arrow before the title */
  onBack?: () => void;
  /** @default 'Back' */
  backTooltip?: string;
  /** Rendered immediately next to the title (e.g. a task action button) */
  actions?: React.ReactNode;
  /** Shows a skeleton in place of the title/meta while the record is still loading */
  isLoading?: boolean;
  /** Opt-in: removes the body's padding so a docked child (e.g. a tab rail) can sit flush
   *  against its left/top edges. The child then owns its own internal spacing. @default false */
  flushBody?: boolean;
  children: React.ReactNode;
}

export const RecordDetailLayout: React.FC<RecordDetailLayoutProps> = ({
  title,
  meta = [],
  onBack,
  backTooltip = 'Back',
  actions,
  isLoading = false,
  flushBody = false,
  children,
}) => {
  const classes = useStyles();

  return (
    <Box className={classes.root}>
      <Box className={classes.banner}>
        {onBack && (
          <>
            <Tooltip title={backTooltip} placement="bottom">
              <IconButton
                className={classes.backButton}
                size="small"
                onClick={onBack}
                aria-label={backTooltip}
              >
                <ArrowBackIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <div className={classes.backDivider} />
          </>
        )}

        <Box className={classes.headerMain}>
          {isLoading ? (
            <SkeletonLoader variant="text" rows={2} />
          ) : (
            <>
              <Box className={classes.titleRow}>
                <Typography className={classes.title}>{title}</Typography>
                {actions && <Box className={classes.actions}>{actions}</Box>}
              </Box>
              {meta.length > 0 && (
                <Box className={classes.metaRow}>
                  {meta.map((item, index) => (
                    <Chip
                      key={index}
                      size="small"
                      className={classes.metaChip}
                      label={(
                        <>
                          <span className={classes.metaChipLabel}>{item.label}</span>
                          <span className={classes.metaChipValue} style={{ color: item.valueColor }}>
                            {item.value}
                          </span>
                        </>
                      )}
                    />
                  ))}
                </Box>
              )}
            </>
          )}
        </Box>
      </Box>

      <Box className={flushBody ? `${classes.body} ${classes.bodyFlush}` : classes.body}>{children}</Box>
    </Box>
  );
};
